export type ConductorHelpScreen = 'portfolio' | 'inbox' | 'cadence' | 'approvals' | 'outbox' | 'reporting' | 'instance'

export const CONDUCTOR_HELP: Record<ConductorHelpScreen, Record<string, string>> = {
  portfolio: {
    'Latest Sweep':
      'Last gate evaluation run. PASS = predicate true; FAIL = blocker with a failure class; UNKNOWN = missing data or a precondition gate did not pass (UNKNOWN never opens a playbook).',
    'Active Workflows':
      'Playbook instances currently in flight (ACTIVE). Total includes paused / blocked / completed / cancelled.',
    'Active Work Items':
      'Open + in-progress human tasks from playbook steps. Items resolve when their completion gate PASSes.',
    'Approvals Waiting':
      'APPROVE-tier outbox actions with no approvedAt. Approving makes the action claimable on the next executor tick; rejecting cancels it permanently.',
    Playbook: 'Named workflow definition (e.g. tenant-account-closeout).',
    Stage: 'Current stage in that playbook (closeout: settlement steps then close).',
    Subject: 'Entity the instance is about (TENANT_ACCOUNT + account).',
    'Open items': 'Count of OPEN / IN_PROGRESS work items on that instance.',
    'Outbox status':
      'Lifecycle of a proposed action: PENDING → CLAIMED → EXECUTING → AWAITING_RECONCILE → SUCCEEDED / FAILED / CANCELLED. Each status opens the Outbox list filtered to that status.',
  },
  inbox: {
    Item: 'Title. Expand the row for the full playbook-step description.',
    Role: 'Owner role the step assigns (not a person).',
    Type: 'TASK = do the work; APPROVAL = human decision; ISSUE / HOLD = blocker / pause.',
    Status: 'OPEN / IN_PROGRESS / RESOLVED / CANCELLED / EXPIRED.',
    Due: 'SLA due date. Red = overdue.',
    Step: 'Playbook step key this item belongs to.',
    Assignee: 'Person currently on the item (King Energy email). Claim, Unclaim, and Reassign write through the engine; the item still resolves when its gate PASSes.',
  },
  cadence: {
    Account:
      'King account number. Opens the tenant page in a new tab with that service account selected. Search matches account number or name.',
    Name: 'Tenant account name from OneBill.',
    'NGOM / VNEM':
      'Accounts whose name contains the word NGOM or VNEM (KE solar-meter utility accounts) stay off this list. They are not tenants.',
    'Test LLC':
      'Seed QA accounts 1211130-0 (Test LLC) and 1211130-1 (Test LLC 2) stay off this list. Other accounts with Test in the name stay visible.',
    Why: 'Sentence from the API for the upstream FAIL gate. Show it as returned; do not remap from billing status.',
    Grid: 'Grid bill cycle end from this sweep (UTC calendar day). Taken from the why gate first, then other cadence gates if missing.',
    'KE invoice':
      'KE invoice cycle end from this sweep (UTC calendar day). Taken from the why gate first, then other cadence gates if missing.',
    Linked:
      'Scraper portal linkage. Yes (3d) means linked, with days since the last scraper run. Dash means the sweep did not report linkage.',
    Utility:
      'Utility from this sweep (PGE, SCE, SDGE). Taken from the why gate first, then other cadence gates if missing. On invoice-lag rows this is often the feed gate.',
    'Billing status':
      'ACTIVE / ONHOLD / BLOCKED chip, plus billing-status reason chips (for example WRONG_TARIFF). Status is not why the account is on this list.',
    'Open steps': 'Open cadence steps on the account: feed, invoice, coherent, salvage.',
    Owner:
      'The column is the primary-why step owner. The Owner filter also keeps rows whose open steps are owned by that role: feed, invoice, and coherent are billing-ops; salvage is collections.',
    Assignee: 'Person currently assigned, if any. King Energy email domain is omitted.',
    Due: 'Earliest open due date. Red = that date has passed.',
    'Past due date':
      'Keeps rows whose earliest open Due date is already in the past. This is the Due column, not the Why overdue-days number and not Grid bill overdue.',
    Workflow: 'Opens the cadence playbook instance in a new tab.',
    'Invoice Tracker':
      'The Retool Invoice Tracker is frozen. Accounts with open billing-cadence-triage work and the Why sentence live on this Cadence list, not on Invoice Tracker Stalled.',
    grid_bill_overdue: 'Grid bill is overdue. Utility vs scraper comes from sideHint when present.',
    no_grid_bill_ever: 'Never received a grid bill.',
    not_invoiced_bill_available: 'Invoice lag: grid bill is here; KE invoice is not.',
    not_invoiced_no_bill: 'Invoice is overdue; no grid bill far enough ahead to invoice.',
    never_invoiced: 'Onboarding complete, billing never started.',
    invoice_overdue: 'KE invoice is overdue.',
    no_ke_invoice: 'KE invoice was never issued.',
    reason_no_longer_holds: 'Billing status reasons do not match facts.',
    stopped_without_reason: 'Billing status reasons do not match facts.',
    missing_mechanical_reason: 'Billing status reasons do not match facts.',
    chronically_unbilled: 'Chronically unbilled; decide close or salvage.',
  },
  approvals: {
    'Needs approval': 'APPROVE-tier action not yet decided.',
    'Awaiting execution': 'Already approved; waiting for the executor (still off until Stage 5).',
    Tool: 'Executor tool that will run (e.g. onebill.close_tenant_account).',
    Autonomy: 'AUTO = fire after delay; APPROVE = human first; HUMAN_ONLY = never auto.',
  },
  outbox: {
    Created: 'When the executor row was proposed.',
    'Action type': 'Playbook or agent action key (for example reconcile-billing-status-reasons). Filter is a contains match.',
    Tool: 'Executor tool that ran or will run (for example onebill.reconcile_billing_status).',
    Status: 'PENDING / CLAIMED / EXECUTING / AWAITING_RECONCILE / SUCCEEDED / FAILED / CANCELLED.',
    'Last error': 'Latest executor error, including blast-radius deferral text. Empty on success.',
    Autonomy:
      'AUTO = fire after delay; APPROVE = human first; HUMAN_ONLY = never auto. HUMAN_ONLY FAILED rows can be cancelled, not retried.',
    Subject: 'Workflow subject cuid. Opens the instance. Approvals stays the human APPROVE queue.',
    Actions:
      'Retry action re-queues a FAILED AUTO or APPROVE row with a new unique idempotency key. Cancel action is terminal and does not execute. Both need conductor:approve.',
  },
  reporting: {
    'Time in stage':
      'Days since the instance last entered this stage (from WORKFLOW_OPENED / WORKFLOW_TRANSITIONED).',
    'SLA timers scheduled': 'Timers not yet fired.',
    Fired: 'Timers that fired in the trailing window.',
    Escalated: 'Timers that escalated in the trailing window.',
    Overdue: 'Open/in-progress work items past dueAt.',
  },
  instance: {
    Playbook: 'Named workflow definition and pinned version.',
    Subject:
      'Tenant account this instance is about. Links to OneBill admin when account number and tenant cuid are present.',
    Stage: 'Current stage in the pinned playbook.',
    Status: 'Instance lifecycle: ACTIVE / PAUSED / BLOCKED / COMPLETED / CANCELLED.',
    'Blocked reason': 'Why the instance is BLOCKED, when set.',
    Steps:
      'Playbook steps with completion gate and status (PENDING / READY / IN_PROGRESS / WAITING / FAILED / DONE / SKIPPED).',
    'Gate verdicts': 'Latest sweep result per gate for this subject (PASS / FAIL / UNKNOWN).',
    'Work items':
      'Human tasks opened for steps on this instance. Open items can be claimed, unclaimed, or reassigned; they still resolve when the step gate PASSes.',
    Timers: 'SLA / escalation timers for steps; sovereign deadlines are marked.',
    'Recent events': 'Latest engine/human/executor audit events on this instance.',
  },
}

export function conductorHelpEntries(screen: ConductorHelpScreen): Array<{ term: string, definition: string }> {
  return Object.entries(CONDUCTOR_HELP[screen]).map(([term, definition]) => ({ term, definition }))
}
